import { useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

export type Suggestion = { value: string; label: string; hint?: string };

interface Props {
  value: string;
  onChange: (v: string) => void;
  suggestions: Suggestion[];
  placeholder?: string;
  limit?: number;
}

/** Search input that pre-populates matching suggestions (clients, emails, services). */
const SearchAutocomplete = ({ value, onChange, suggestions, placeholder, limit = 8 }: Props) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef<number | null>(null);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    const pool = q
      ? suggestions.filter(
          (s) => s.label.toLowerCase().includes(q) || s.value.toLowerCase().includes(q),
        )
      : suggestions;
    return pool.slice(0, limit);
  }, [value, suggestions, limit]);

  const pick = (s: Suggestion) => {
    onChange(s.value);
    setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        placeholder={placeholder}
        className="rounded-none"
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (!open || matches.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => (a + 1) % matches.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => (a - 1 + matches.length) % matches.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            pick(matches[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && matches.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto border border-border bg-popover shadow-lg">
          {matches.map((s, i) => (
            <li key={`${s.value}-${i}`}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (blurTimer.current) window.clearTimeout(blurTimer.current);
                  pick(s);
                }}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2 text-sm font-light ${
                  i === active ? "bg-accent text-accent-foreground" : "text-foreground"
                }`}
              >
                <span className="block truncate">{s.label}</span>
                {s.hint && (
                  <span className="block truncate text-xs text-muted-foreground">{s.hint}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchAutocomplete;
